import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Ion } from "@/components/Ion";
import { CountBadge, useBillingOn, useNewLeads } from "@/lib/billing";
import { DESKTOP_BREAKPOINT, useTheme, useWindowWidth } from "@/lib/theme";

const LOGO = "/alr-ops-logo.png";
const A = "admin", F = "franchisee", M = "machine_owner", R = "rental_customer", E = "franchisee_employee";
const n = (key, label, icon, route, roles) => ({ key, label, icon, route, roles });

// Same order, labels, icons and roles as frontend/src/components/DesktopShell.tsx
const NAV = [
  n("dashboard", "Dashboard", "grid-outline", "/dashboard", [A, F, M]),
  n("rental_home", "Rental Portal", "grid-outline", "/lso-rental", [R]),
  n("guide", "ALR Guide", "compass-outline", "/guide?mode=help", [A, F]),
  n("ai", "ALR Cleaning Agent", "sparkles-outline", "/ai-assistant", [A, F, M, R]),
  n("health_check", "Franchise Health Check", "fitness-outline", "/health-check", [A, F, E]),
  n("playbook", "ALR Playbook", "book-outline", "/playbook", [A, F, E]),
  n("customer_community", "Community Chat", "chatbubbles-outline", "/customer-community", [A, M]),
  n("customer_team", "My Team", "people-circle-outline", "/customer-team", [A, M]),
  n("quote_builder", "Project Quote Builder", "calculator-outline", "/sales-coach/quote-builder", [A, F]),
  n("cleaning_settings_public", "Laser Cleaning Settings", "bulb-outline", "/cleaning-settings", [M, R]),
  n("sales_coach", "AI Sales Coach", "trending-up", "/sales-coach", [A, F]),
  n("community", "Franchisee Community", "people-outline", "/community", [A, F]),
  n("launch_training", "Launch Training", "rocket-outline", "/launch-training", [A, F]),
  n("lso", "Laser Safety Course", "school-outline", "/lso", [A, F, M]),
  n("lso_rental_course", "Laser Safety Course", "school-outline", "/lso-course", [R]),
  n("ops", "Operations Manual", "library-outline", "/operations", [A, F]),
  n("my_presets", "Laser Cleaning Settings", "bulb-outline", "/my-presets", [A, F]),
  n("laser_machines", "Machines & Calibration", "hardware-chip-outline", "/laser-machines", [A, F]),
  n("equipment_manuals", "Equipment Manuals", "construct-outline", "/section/equipment_manuals", [A, F, M]),
  n("safety_resources", "Safety Resources", "shield-checkmark-outline", "/section/safety_resources", [A, F, M, R]),
  n("store", "ALR Store", "cart-outline", "/store", [A, F]),
  n("marketing", "Marketing", "megaphone-outline", "/section/marketing", [A, F]),
  n("training_videos", "Training Videos", "videocam-outline", "/section/training_videos", [A, F]),
  n("forms", "Forms & Submissions", "document-text-outline", "/section/forms", [A, F]),
  n("support", "Support", "headset-outline", "/support", [A, F, M, R]),
  n("feedback", "Suggest a Feature", "bulb-outline", "/feedback", [A, F, M, R]),
];
const ADMIN_NAV = [
  n("admin", "Admin Console", "shield-outline", "/admin", [A]),
  n("admin_billing", "Franchisee Invoicing", "wallet-outline", "/admin/billing", [A]),
  n("bookkeeping", "Bookkeeping", "calculator-outline", "/bookkeeping", [A]),
  n("safety_admin", "Safety Queue", "warning-outline", "/admin/safety", [A]),
  n("community_mod", "Community Moderation", "shield-checkmark-outline", "/admin/community-moderation", [A]),
  n("deploy_checklist", "Launch Checklist", "rocket-outline", "/admin/deploy-checklist", [A]),
];
const PUBLIC_ROUTES = ["/", "/index", "/terms", "/privacy"];
const PORTAL = { machine_owner: "MACHINE OWNER", admin: "CORPORATE ADMIN", rental_customer: "RENTAL CUSTOMER" };

const NavItem = ({ item, active, theme, onGo, badge = 0 }) => (
  <button onClick={() => onGo(item.route)} data-testid={`sidebar-${item.key}`}
    className="mx-2 my-px flex w-[calc(100%-16px)] items-center gap-3 rounded-lg px-5 py-[11px] text-left transition-colors hover:bg-white/[0.03]"
    style={active ? { background: `${theme.accent}1A` } : undefined}>
    <Ion name={item.icon} size={18} color={active ? theme.accent : "#A8A8B8"} />
    <span className="flex-1 text-[13px] font-medium" style={{ color: active ? theme.text : "#A8A8B8" }}>{item.label}</span>
    <CountBadge n={badge} testId={`sidebar-${item.key}-badge`} />
  </button>
);

export const DesktopShell = ({ children }) => {
  const width = useWindowWidth();
  const { user, logout, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const billingOn = useBillingOn(user);
  const newLeads = useNewLeads(billingOn && user?.role === "franchisee" && !user?.actual_role);
  if (width < DESKTOP_BREAKPOINT) return children;
  if (PUBLIC_ROUTES.some((p) => pathname === p || pathname === `${p}/`) || loading || !user) return children;

  let items = NAV.filter((x) => x.roles.includes(user.role));
  if (user.role === "machine_owner" && user.parent_owner_id) items = items.filter((x) => x.key !== "customer_team");
  if (user.role === "admin") items = [...items, ...ADMIN_NAV];
  if (billingOn && user.role === "franchisee") items = [items[0], n("billing", "Invoicing & Payments", "wallet-outline", "/billing", [F]), n("billing_calendar", "Calendar", "calendar-outline", "/billing/calendar", [F]), ...items.slice(1)];
  if (billingOn && user.role === "franchisee_employee") items = [items[0], n("my_schedule", "My Schedule", "calendar-outline", "/my-schedule", [E]), ...items.slice(1)];
  const navPath = pathname === "/sales-coach/door-quote" ? "/sales-coach/quote-builder" : pathname;
  const activeKey = items.filter((x) => navPath === x.route || navPath.startsWith(`${x.route}/`)).sort((a, b) => b.route.length - a.route.length)[0]?.key;

  return (
    <div className="flex min-h-screen" style={{ background: theme.bg }}>
      <aside className="sticky top-0 flex h-screen w-[264px] shrink-0 flex-col border-r border-[#0F0F11] bg-[#0A0A0A] pt-6" data-testid="desktop-sidebar">
        <div className="flex flex-col items-start border-b border-[#0F0F11] bg-[#0F0F11] px-5 pb-4">
          <img src={LOGO} alt="ALR Ops" className="mb-1 h-auto w-[180px]" data-testid="sidebar-logo" />
          <span className="mt-2.5 rounded-full border border-[#2A2A2E] bg-[#1A1A1D] px-2 py-[3px] text-[9px] font-bold tracking-[1px]" style={{ color: theme.accent }} data-testid="sidebar-role-badge">{PORTAL[user.role] || "FRANCHISEE PORTAL"}</span>
        </div>
        <nav className="alr-scroll flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
          {items.map((item) => <NavItem key={item.key} item={item} active={item.key === activeKey} theme={theme} onGo={navigate} badge={item.key === "billing" ? newLeads : 0} />)}
        </nav>
        <div className="border-t border-[#0F0F11] px-5 py-3.5">
          <p className="truncate text-[13px] font-bold" style={{ color: theme.text }}>{user.full_name || user.email}</p>
          <p className="mb-2.5 mt-0.5 truncate text-[11px]" style={{ color: theme.textSecondary }}>{user.email}</p>
          <button onClick={logout} data-testid="sidebar-logout" className="flex items-center gap-1.5 py-1.5">
            <Ion name="log-out-outline" size={16} color={theme.error} /><span className="text-xs font-semibold" style={{ color: theme.error }}>Sign out</span>
          </button>
          <p className="mt-3 text-[9px] tracking-[0.5px]" style={{ color: theme.textSecondary }}>© 2026 ALR · Authorized use only</p>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 justify-center" style={{ background: theme.bg }}>
        <div className="w-full max-w-[1280px]" style={{ background: theme.bg }}>{children}</div>
      </div>
    </div>
  );
};
